import { api } from '@/shared/api/client'
import { ApiError } from '@/shared/api/problem'
import { getUnit } from '@/features/geography/api'
import { lookupVoter, voterDetail } from '@/features/voters/api'
import { qs, type QueryValue } from '@/shared/lib/query'
import type {
  BatchActionReport, CursorPage, EligibilityReport, GateCatalogueEntry,
  TransferBatchResponse, TransferDetailResponse, TransferLookupResponse,
  TransferAnalysisResponse, TransferNoteResponse, TransferRequestResponse,
  TransferStatisticsResponse,
} from '@/shared/api/types'

/**
 * The transfer workflow.
 *
 * ## The one sentence that governs every string in this feature
 *
 * **This system does not transfer voters.** Under Kenyan law that is an IEBC
 * process. What happens here is: a voter asks to move, they consent, the request
 * is checked and reviewed and approved, it is *lodged with the IEBC*, the IEBC
 * confirms it, and only then do we update **our record** of where they are
 * registered. `RECORDED` means "we recorded that this happened".
 *
 * The backend enforces this with a `TerminologyTest` that fails the build on any
 * active-voice string. The web has no such test, so every label in this file was
 * written deliberately: "Lodge with IEBC", not "Transfer"; "Record the outcome",
 * not "Move the voter". A system that appears to move voters between polling
 * stations will be accused of doing exactly that, and the interface should make
 * the accusation obviously false.
 */

// ── requests ────────────────────────────────────────────────────────────────

export const listTransfers = (params: Record<string, QueryValue>, cursor?: string, size = 50) =>
  api.get<CursorPage<TransferRequestResponse>>(
    `/transfers${qs({ ...params, cursor, size })}`)

export const getTransfer = (id: string) =>
  api.get<TransferDetailResponse>(`/transfers/${id}`)

export const createTransfer = (body: unknown) =>
  api.post<TransferRequestResponse>('/transfers', body)

/** `If-Match` carries the row version. DRAFT only, server-side. */
export const updateTransfer = (id: string, body: unknown, version: number) =>
  api.patch<TransferRequestResponse>(`/transfers/${id}`, body, {
    headers: { 'If-Match': String(version) },
  })

export const transferStatistics = (params: Record<string, QueryValue> = {}) =>
  api.get<TransferStatisticsResponse>(`/transfers/statistics${qs(params)}`)

// ── consent ─────────────────────────────────────────────────────────────────

/**
 * The artefact that makes a request legitimate — no request leaves DRAFT
 * without one, enforced in the service AND by a check constraint.
 *
 * Requires `relocation:create` **and** `voter:consent`: it writes on both sides,
 * because the voter's own consent record has to be complete in one place.
 */
export const captureConsent = (id: string, body: unknown) =>
  api.post<TransferDetailResponse>(`/transfers/${id}/consent`, body)

/**
 * Revoking auto-withdraws the request. Legal at any point before RECORDED.
 *
 * The reason goes in the **body**, not the query string — `ReasonRequest` is what
 * the endpoint declares, and a sentence about why a named person took back their
 * consent has no business in a URL, where it lands in access logs and history.
 */
export const revokeConsent = (id: string, reason: string) =>
  api.del<void>(`/transfers/${id}/consent`, { body: { reason } })

// ── lifecycle ───────────────────────────────────────────────────────────────
// Every one of these is a state transition the backend's TransferStateMachine
// decides. Call them only when `allowedDecisions` says so — see the note on
// TransferDetailResponse.

export const submitTransfer = (id: string) =>
  api.post<EligibilityReport>(`/transfers/${id}/submit`)

export const recheckTransfer = (id: string) =>
  api.post<EligibilityReport>(`/transfers/${id}/recheck`)

export const waiveGate = (id: string, gate: string, reason: string) =>
  api.post<EligibilityReport>(`/transfers/${id}/gates/${gate}/waive`, { reason })

export const assignTransfer = (id: string, assigneeUserId: string, notes?: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/assign`, { assigneeUserId, notes })

export const reviewTransfer = (id: string, notes?: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/review`, { notes })

export const approveTransfer = (id: string, version: number, notes?: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/approve`, { notes },
    { headers: { 'If-Match': String(version) } })

export const rejectTransfer = (id: string, reasonCode: string, detail: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/reject`, { reasonCode, detail })

export const withdrawTransfer = (id: string, reason: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/withdraw`, { reason })

export const appealTransfer = (id: string, rationale: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/appeal`, { rationale })

/** No IEBC reference, no lodgement — refused in the service and by a constraint. */
export const lodgeTransfer = (id: string, iebcReference: string, notes?: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/lodge`, { iebcReference, notes })

export const confirmTransfer = (id: string, notes?: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/confirm`, { notes })

export const failAtIebc = (id: string, reason: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/fail`, { reason })

/** The only call in this feature that touches a voter, and only via VoterPlacementPort. */
export const recordTransfer = (id: string, notes?: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/record`, { notes })

/** Writes a compensating placement-history row. Never deletes the original. */
export const reverseTransfer = (id: string, reason: string) =>
  api.post<TransferRequestResponse>(`/transfers/${id}/reverse`, { reason })

// ── analysis ────────────────────────────────────────────────────────────────

/**
 * Everything the Analysis screen draws, computed server-side.
 *
 * `from`/`to` are dates, not instants: the day boundary belongs to the deployment's
 * time zone (`app.timezone`), and only the server knows it. Sending instants from a
 * browser would bucket a request raised at 01:00 in Nairobi into the previous day for
 * anyone whose laptop is set to UTC.
 */
export const transferAnalysis = (params: Record<string, QueryValue>) =>
  api.get<TransferAnalysisResponse>(`/transfers/analysis${qs(params)}`)

// ── the field screen ────────────────────────────────────────────────────────

/**
 * Look a person up by national ID and answer, in one place: who are they, where are
 * they registered right now, and is anything already open for them.
 *
 * **Why an ID and not a name.** A mobilizer is standing at a door with a national
 * register of tens of millions behind them. Names collide — several people in one ward
 * can share one — and a name search asks the operator to disambiguate strangers. The ID
 * is the key that person is holding in their hand.
 *
 * **Why POST.** A national ID must never ride in a URL. In a query string it lands in
 * access logs, browser history, proxy logs and `Referer` headers. `POST /voters/lookup`
 * exists for exactly this reason and this follows it.
 *
 * **Why the fallback.** `POST /transfers/lookup` is prompt 28. Until it lands, the same
 * answer is assembled from three calls the API already serves — lookup, voter, unit —
 * so this screen works today and gets faster, not different, when the backend catches
 * up. The fallback fires only on 404/405: any other failure is a real failure and is
 * thrown.
 */
export async function fieldLookup(nationalId: string): Promise<TransferLookupResponse> {
  let found: TransferLookupResponse | null = null
  try {
    found = await api.post<TransferLookupResponse>('/transfers/lookup', { nationalId })
  } catch (e) {
    // Only a missing endpoint falls back. Any other failure is a real failure and is
    // thrown, because silently degrading to a second-best answer hides an outage.
    if (!(e instanceof ApiError) || (e.status !== 404 && e.status !== 405)) throw e
    return assembleLookup(nationalId)
  }

  if (found.found) return found

  // `/transfers/lookup` answers one question — does OUR register know them. When it
  // does not, the second question is the whole reason the registration path exists:
  // does the government identity service know them. That is `/voters/lookup`, which is
  // the endpoint that reaches eCitizen, and it is only ever called on a miss.
  return { ...found, upstream: await upstreamOf(nationalId) }
}

/** What eCitizen says, as a proposal. Never applied without the operator accepting it. */
async function upstreamOf(nationalId: string): Promise<TransferLookupResponse['upstream']> {
  const hit = await lookupVoter({ nationalId }).catch(() => null)
  if (!hit) return { found: false, source: 'NONE', verified: false }
  if (!hit.found || !hit.prefill) return { found: false, source: hit.source, verified: false }
  return {
    found: true,
    source: hit.source,
    firstName: hit.prefill.firstName,
    middleName: hit.prefill.middleName,
    surname: hit.prefill.surname,
    birthYear: hit.prefill.birthYear,
    genderCode: hit.prefill.genderCode,
    verified: Boolean(hit.upstreamVerified),
  }
}

/**
 * The same answer for someone picked by NAME rather than by ID.
 *
 * The name path used to skip this and assume "picked from the register ⇒ transfer",
 * which is wrong twice: the register also holds citizens who are not on the roll, and it
 * says nothing about whether a request is already open. Both paths produce the same
 * object, so both get the same kind, the same placement and the same refusal.
 */
export async function lookupByVoterId(voterId: string): Promise<TransferLookupResponse> {
  return fromVoterRecord(voterId)
}

/** The fallback path, for a backend without `/transfers/lookup`. */
async function assembleLookup(nationalId: string): Promise<TransferLookupResponse> {
  const hit = await lookupVoter({ nationalId })
  if (!hit.alreadyRegistered || !hit.existingVoterId) {
    return { found: false, upstream: await upstreamOf(nationalId) }
  }
  return fromVoterRecord(hit.existingVoterId)
}

/** Builds the lookup shape from the voter record plus one geography call. */
async function fromVoterRecord(voterId: string): Promise<TransferLookupResponse> {
  const voter = await voterDetail(voterId)

  const names: Record<string, string> = {}
  if (voter.pollingStationId) {
    // One call: the unit detail carries its ancestors root-first, which IS the
    // County → Constituency → Ward → Station line this screen has to show.
    const unit = await getUnit(voter.pollingStationId).catch(() => null)
    if (unit) {
      for (const a of unit.ancestors) names[a.unitType] = a.name
      names[unit.unit.unitType] = unit.unit.name
    }
  }

  // Whether a request is already open is the caller's own state, so ask for it
  // directly rather than guessing from the voter record.
  const open = await listTransfers({ voterId, size: 5 })
    .then((p) => p.content.find((t) => OPEN_STATUSES.includes(t.status)) ?? null)
    .catch(() => null)

  return {
    found: true,
    voterId: voter.id,
    displayName: voter.displayName,
    voterNumber: voter.voterNumber ?? null,
    nationalIdMasked: null,          // this path has no masked value to show
    registrationStatusCode: voter.registrationStatusCode,
    pollingStationId: voter.pollingStationId ?? null,
    countyName: names.COUNTY ?? null,
    constituencyName: names.CONSTITUENCY ?? null,
    wardName: names.WARD ?? null,
    pollingStationName: names.POLLING_STATION ?? null,
    openTransferRequestId: open?.id ?? null,
    openTransferReference: open?.reference ?? null,
    openTransferStatus: open?.status ?? null,
    openTransferKind: open?.kind ?? null,
  }
}

/**
 * The four flat name columns, as the hierarchy they are. Levels with no name are
 * dropped rather than rendered blank — an unregistered citizen has a ward and no
 * station, and an empty chip would read as a station we failed to load.
 */
export function placementOf(l: TransferLookupResponse): { level: string; name: string }[] {
  return ([
    ['COUNTY', l.countyName],
    ['CONSTITUENCY', l.constituencyName],
    ['WARD', l.wardName],
    ['POLLING_STATION', l.pollingStationName],
  ] as const)
    .filter(([, name]) => Boolean(name))
    .map(([level, name]) => ({ level, name: name as string }))
}

/** Not at rest — the statuses in which a second request for the same person is refused. */
export const OPEN_STATUSES: string[] = [
  'DRAFT', 'SUBMITTED', 'VERIFYING', 'UNDER_REVIEW', 'APPROVED', 'LODGED', 'UNDER_APPEAL',
]

/**
 * Search the queue by national ID. POST for the same reason `fieldLookup` is a POST.
 * An unknown ID is an empty page, never a 404 — a 404 would be an answer about whether
 * a real person exists anywhere in the system.
 */
export const searchTransfersByNationalId = (
  nationalId: string, params: Record<string, QueryValue> = {}, cursor?: string, size = 50,
) =>
  api.post<CursorPage<TransferRequestResponse>>('/transfers/search',
    { nationalId, ...params, cursor, size })

/** What gates exist and which kind each applies to. Cached for the session. */
export const gateCatalogue = () =>
  api.get<GateCatalogueEntry[]>('/transfers/gate-catalogue')

// ── trail ───────────────────────────────────────────────────────────────────

export const transferDecisions = (id: string) =>
  api.get<import('@/shared/api/types').DecisionResponse[]>(`/transfers/${id}/decisions`)

export const transferNotes = (id: string) =>
  api.get<TransferNoteResponse[]>(`/transfers/${id}/notes`)

export const addTransferNote = (id: string, body: string, internal = true) =>
  api.post<TransferNoteResponse>(`/transfers/${id}/notes`, { body, internal })

// ── batches ─────────────────────────────────────────────────────────────────

export const listBatches = (params: Record<string, QueryValue> = {}) =>
  api.get<TransferBatchResponse[]>(`/transfer-batches${qs(params)}`)

export const getBatch = (id: string) =>
  api.get<TransferBatchResponse>(`/transfer-batches/${id}`)

export const batchMembers = (id: string, cursor?: string, size = 50) =>
  api.get<CursorPage<TransferRequestResponse>>(
    `/transfer-batches/${id}/members${qs({ cursor, size })}`)

export const createBatch = (body: unknown) =>
  api.post<TransferBatchResponse>('/transfer-batches', body)

/** Submits the members whose gates pass; reports the rest rather than hiding them. */
export const submitBatch = (id: string) =>
  api.post<BatchActionReport>(`/transfer-batches/${id}/submit`)

export const closeBatch = (id: string, reason: string) =>
  api.post<BatchActionReport>(`/transfer-batches/${id}/close`, { reason })

// ── labels ──────────────────────────────────────────────────────────────────
// Every map falls back to `humanise(code)` at the call site, so a backend that
// adds a code renders it rather than showing a blank.

export const TRANSFER_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  VERIFYING: 'Checking eligibility',
  INELIGIBLE: 'Ineligible',
  UNDER_REVIEW: 'Under review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
  EXPIRED: 'Expired',
  UNDER_APPEAL: 'Under appeal',
  LODGED: 'Lodged with IEBC',
  CONFIRMED: 'Confirmed by IEBC',
  FAILED_AT_IEBC: 'Refused by IEBC',
  RECORDED: 'Recorded on our register',
  REVERSED: 'Reversed',
}

/** Where each status sits: open work, done, or stopped. Drives the dot only. */
export const STATUS_TONE: Record<string, 'open' | 'good' | 'bad' | 'warn'> = {
  DRAFT: 'open', SUBMITTED: 'open', VERIFYING: 'open', UNDER_REVIEW: 'open',
  APPROVED: 'open', LODGED: 'open', UNDER_APPEAL: 'open',
  CONFIRMED: 'good', RECORDED: 'good',
  INELIGIBLE: 'warn', EXPIRED: 'warn', WITHDRAWN: 'warn', REVERSED: 'warn',
  REJECTED: 'bad', FAILED_AT_IEBC: 'bad',
}

export const INTAKE_CHANNEL_LABEL: Record<string, string> = {
  FIELD: 'Field', OFFICE: 'Office', SELF_SERVICE: 'Self-service',
  BULK: 'Bulk', SYSTEM_SUGGESTION: 'System suggestion',
}

/**
 * The reason code a registration carries. `reasonCode` is mandatory on every request and
 * "why are you registering" has one answer, so the UI does not ask — it sends this and
 * shows the label read-only.
 */
export const REGISTRATION_REASON_CODE = 'NEW_REGISTRATION'

export const TRANSFER_REASON_LABEL: Record<string, string> = {
  NEW_REGISTRATION: 'Not yet registered',
  VOTER_REQUESTED: 'Voter requested it',
  RESIDENCE_CHANGED: 'Residence changed',
  RESIDENCE_MISMATCH: 'Residence does not match the station',
  NEARER_STATION_AVAILABLE: 'A nearer station is available',
  STATION_OVER_CAPACITY: 'Station over capacity',
  ACCESSIBILITY_NEED: 'Accessibility need',
  STATION_DISSOLVED: 'Station dissolved',
  BOUNDARY_REVIEW: 'Boundary review',
  DATA_CORRECTION: 'Data correction',
  EMPLOYMENT_RELOCATION: 'Moved for work',
  SECURITY_CONCERN: 'Security concern',
}

export const REJECTION_REASON_LABEL: Record<string, string> = {
  VOTER_DECLINED: 'The voter declined',
  CONSENT_INVALID: 'Consent was not valid',
  INSUFFICIENT_EVIDENCE: 'Not enough evidence',
  TARGET_AT_CAPACITY: 'Target station is full',
  TARGET_INACTIVE: 'Target station is not active',
  OUT_OF_SCOPE: 'Outside your scope',
  DUPLICATE: 'Duplicate request',
  IEBC_WINDOW_CLOSED: 'The IEBC window is closed',
  IDENTITY_UNVERIFIED: 'Identity not verified',
  POLICY: 'Policy',
}

export const CONSENT_METHOD_LABEL: Record<string, string> = {
  SIGNATURE: 'Signature', OTP: 'One-time code', VERBAL_WITNESSED: 'Verbal, witnessed',
  WEB_FORM: 'Web form', USSD: 'USSD', LETTER: 'Letter',
}

export const BATCH_KIND_LABEL: Record<string, string> = {
  STATION_DISSOLVED: 'Station dissolved', BOUNDARY_REVIEW: 'Boundary review',
  DATA_CORRECTION: 'Data correction', OUTREACH_DRIVE: 'Outreach drive',
  SUGGESTION_RUN: 'Suggestion run',
}

/**
 * The sixteen gates, in the order the backend runs them, each with what it means
 * in words an operator can act on. A gate that fails and says only its code is a
 * dead end; the point of naming all sixteen was that the UI can say which.
 */
export const GATE_LABEL: Record<string, { label: string; hint: string }> = {
  VOTER_EXISTS: { label: 'Voter exists', hint: 'The voter resolves and is active on the register.' },
  VOTER_REGISTERED: { label: 'Voter is registered', hint: 'Registration status is REGISTERED.' },
  VOTER_NOT_DECEASED: { label: 'Not recorded deceased', hint: 'No deceased status, and no contact recorded them as deceased in the last 90 days.' },
  IDENTITY_VERIFIED: { label: 'Identity verified', hint: 'Verification reaches the configured minimum. A failed verification never passes, whatever the minimum is.' },
  // Registration-only. Both are absent from a transfer's report, which is why the panel
  // renders the gates the SERVER sent rather than this map's key order.
  AGE_ELIGIBLE: { label: 'Old enough to register', hint: 'At least the configured minimum age on the election date. Checked to the year, because the register holds a birth year and not a date.' },
  NOT_ALREADY_REGISTERED: { label: 'Not already registered', hint: 'Someone already on the roll needs a transfer, not a registration.' },
  CONSENT_PRESENT: { label: 'Consent captured', hint: 'A consent artefact exists and has not been revoked.' },
  CONSENT_METHOD_ALLOWED: { label: 'Consent method allowed', hint: 'The method used is permitted for this intake channel.' },
  SOURCE_MATCHES_CURRENT: { label: 'Source station still current', hint: 'The voter has not moved since this was drafted.' },
  TARGET_DIFFERENT: { label: 'Target differs from source', hint: 'A transfer to the same station is not a transfer.' },
  TARGET_IS_STATION: { label: 'Target is a polling station', hint: 'Not a ward or a constituency.' },
  TARGET_ACTIVE: { label: 'Target is active', hint: 'Not merged, dissolved or proposed.' },
  TARGET_HAS_CAPACITY: { label: 'Target has capacity', hint: 'Registered voters at the target are below the configured ceiling.' },
  NO_OPEN_REQUEST: { label: 'No other open request', hint: 'One open request per voter, enforced by the database.' },
  COOL_OFF_ELAPSED: { label: 'Cool-off elapsed', hint: 'Long enough since this voter last had a transfer recorded.' },
  IEBC_WINDOW_OPEN: { label: 'IEBC window open', hint: 'Now is inside the configured transfer window. An unconfigured window passes.' },
  SCOPE_PERMITTED: { label: 'Within your scope', hint: 'Your scope covers both the source and the target. A transfer reaches across two units.' },
  NOT_SUPPRESSED: { label: 'Not suppressed', hint: 'The voter is not marked do-not-contact for a reason that means we should not act for them.' },
}

export const GATE_ORDER = Object.keys(GATE_LABEL)

/** What a decision did, in the past tense, for the trail. */
export const DECISION_LABEL: Record<string, string> = {
  CREATE: 'Created', CONSENT_CAPTURED: 'Consent captured', SUBMIT: 'Submitted',
  VERIFY: 'Eligibility checked', WAIVE_GATE: 'Gate waived', ASSIGN: 'Assigned',
  REVIEW: 'Taken under review', APPROVE: 'Approved', REJECT: 'Rejected',
  WITHDRAW: 'Withdrawn', APPEAL: 'Appealed', LODGE: 'Lodged with IEBC',
  CONFIRM: 'Confirmed by IEBC', FAIL_AT_IEBC: 'Refused by IEBC',
  RECORD: 'Recorded on our register', REVERSE: 'Reversed', EXPIRE: 'Expired',
  ESCALATE: 'Escalated', COMMENT: 'Comment',
}
